import defaultCoinIcon from '@assets/images/icons/default-coin.svg';
import { defineModel } from 'foca';
import { type ParrotCoinStruct } from 'src/types';
import { parrotClient } from '@libs/parrot-sdk';
import { parrotApi } from '@libs/openapi';
import { OpenapiClientParrot } from 'src/openapi/parrot';

interface coinStateStruct {
  loading: Record<string, boolean>;
  coins: Record<string, ParrotCoinStruct>;
  cacheCoins: ParrotCoinStruct[];
  popularCoins: ParrotCoinStruct[];
  quoteCoins: ParrotCoinStruct[];
}

const initialState: coinStateStruct = {
  loading: {},
  coins: {},
  cacheCoins: [],
  popularCoins: [],
  quoteCoins: [],
};

export const coinParrotModel = defineModel('coin-parrot', {
  initialState,
  methods: {
    // http get
    async getHttpAllCoins() {
      const result = await parrotApi.get('/clmm/coins/all', {});
      let coins: Record<string, ParrotCoinStruct> = {};

      const popularCoins: ParrotCoinStruct[] = [];
      const quoteCoins: ParrotCoinStruct[] = [];
      const newCoins = result.map((coin) => {
        const _coin = {
          ...coin,
          type: coin.coin_type,
          iconUrl: coin.icon_url || defaultCoinIcon,
        };
        coins[coin.coin_type] = _coin;

        if (_coin.is_popular) {
          popularCoins.push(_coin);
        }
        if (_coin.is_quote) {
          quoteCoins.push(_coin);
        }
        return _coin;
      });

      this.setState({
        cacheCoins: newCoins,
        coins,
        popularCoins,
        quoteCoins,
      });

      return newCoins;
    },

    async getHttpCoins(query: OpenapiClientParrot.GetClmmCoinsQuery) {
      const result = await parrotApi.get('/clmm/coins', {
        query: query,
      });

      let _coins: Record<string, ParrotCoinStruct> = {};
      result.result.forEach((coin) => {
        _coins[coin.coin_type] = {
          ...coin,
          type: coin.coin_type,
          iconUrl: coin.icon_url || defaultCoinIcon,
        };
      });
      this.setState((state) => {
        state.coins = {
          ...state.coins,
          ..._coins,
        };
      });

      return result;
    },

    async getHttpCoinsByToken(params: OpenapiClientParrot.GetClmmCoinsByTokenParams) {
      const type = params.token;
      if (this.state.coins[type]) return this.state.coins[type];
      if (this.state.loading[type]) return;

      this.setState((state) => {
        state.loading[type] = true;
      });

      const result = await parrotApi.get('/clmm/coins/{token}', {
        params: params,
      });

      this.setState((state) => {
        state.coins[type] = {
          ...result.coin,
          type: type,
          iconUrl: result.coin.icon_url || defaultCoinIcon,
        };
        state.loading[type] = false;
      });

      return result;
    },

    // rpc
    async getCoin(type: string) {
      if (this.state.coins[type]) return this.state.coins[type];
      if (this.state.loading[type]) return;
      this.setState((state) => {
        state.loading[type] = true;
      });
      const coinMetadata = await parrotClient.getCoinMetadata({
        coinType: type,
      });

      let coinData: ParrotCoinStruct | undefined;
      if (coinMetadata) {
        coinData = {
          ...coinMetadata,
          type: type,
          iconUrl: coinMetadata.iconUrl || defaultCoinIcon,
        };
        this.setState((state) => {
          state.coins[type] = coinData!;
          state.cacheCoins = [...state.cacheCoins, coinData!];
        });
      }

      this.setState((state) => {
        state.loading[type] = false;
      });

      return coinData;
    },
  },
});
